import { DiscoveryApi } from '@backstage/plugin-permission-common';
import {
  AssignTeamRoleParams,
  DeleteTeamRoleParams,
  GetEnabledTeamRolesParams,
  ResolveTeamRoleParams,
  TeamRolesApi,
} from './teamRolesApi';
import {
  TeamRolesConfigurationResponse,
  TeamRolesResolveResponse,
} from './types';
import { ResponseError } from '@backstage/errors';

type SendRequestOptions = {
  path: string;
  method: 'POST' | 'GET' | 'DELETE' | 'PUT';
  body?: BodyInit | null | undefined;
  token?: string;
};

export class TeamRolesClient implements TeamRolesApi {
  constructor(private readonly discoveryApi: DiscoveryApi) {}

  private composeQueryString(queryObject?: any): URLSearchParams {
    const queryString = new URLSearchParams();

    if (!queryObject) {
      return queryString;
    }
    Object.keys(queryObject).forEach(key => {
      if (queryObject[key]) {
        queryString.append(key, queryObject[key] as string);
      }
    });

    return queryString;
  }

  private async sendRequest<T>({
    path,
    method,
    token,
    body,
  }: SendRequestOptions): Promise<T> {
    const baseUrl = `${await this.discoveryApi.getBaseUrl('builder')}/`;
    const url = new URL(path, baseUrl);

    const response = await fetch(url.toString(), {
      method: method,
      headers: token
        ? {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          }
        : { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: body,
    });

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    const text = await response.text();
    if (!text) {
      return undefined as T;
    }

    return JSON.parse(text) as T;
  }

  async resolve({
    teamRole,
    projectUrn,
    mode,
    useFallback,
    options,
  }: ResolveTeamRoleParams): Promise<TeamRolesResolveResponse> {
    const queryString = this.composeQueryString({
      projectUrn,
      mode,
      useFallback: useFallback.toString(),
    });

    return await this.sendRequest<TeamRolesResolveResponse>({
      path: `team-roles/${teamRole}?${queryString}`,
      method: 'GET',
      token: options?.token,
    });
  }

  async addAssignee({
    teamRole,
    projectUrn,
    subject,
    options,
  }: AssignTeamRoleParams): Promise<void> {
    await this.sendRequest({
      path: `team-roles/${teamRole}`,
      method: 'POST',
      body: JSON.stringify({ projectUrn, subject }),
      token: options?.token,
    });
  }

  async deleteAssignee({
    projectUrn,
    subject,
    teamRole,
    options,
  }: DeleteTeamRoleParams): Promise<void> {
    const queryString = this.composeQueryString({
      projectUrn,
      subject,
    });

    await this.sendRequest<TeamRolesResolveResponse>({
      path: `team-roles/${teamRole}?${queryString}`,
      method: 'DELETE',
      token: options?.token,
    });
  }

  async addLimitedAssignee({
    teamRole,
    projectUrn,
    subject,
    options,
  }: AssignTeamRoleParams): Promise<void> {
    await this.sendRequest({
      path: `team-roles/${teamRole}/limited-assignees`,
      method: 'POST',
      body: JSON.stringify({ projectUrn, subject }),
      token: options?.token,
    });
  }

  async deleteLimitedAssignee({
    projectUrn,
    subject,
    teamRole,
    options,
  }: DeleteTeamRoleParams): Promise<void> {
    const queryString = this.composeQueryString({
      projectUrn,
      subject,
    });

    await this.sendRequest<TeamRolesResolveResponse>({
      path: `team-roles/${teamRole}/limited-assignees?${queryString}`,
      method: 'DELETE',
      token: options?.token,
    });
  }

  editLimitedAssignee(_params: AssignTeamRoleParams): Promise<void> {
    throw new Error('Method not implemented.');
  }

  async getEnabledTeamRoles({
    urn,
    options,
  }: GetEnabledTeamRolesParams): Promise<TeamRolesConfigurationResponse> {
    const queryString = this.composeQueryString({ urn });

    return await this.sendRequest<TeamRolesConfigurationResponse>({
      path: `team-roles/by-urn?${queryString}`,
      method: 'GET',
      token: options?.token,
    });
  }
}
